import * as React from "react";
import useSWR from "swr";
import Swal from "sweetalert2";
import { Card, Select, Space, Spin, Typography, Divider, Pagination } from "antd";

import { snackbarStore } from "src/store/snackbarStore";
import { useCampaignStore } from "src/store/campaign/campaign.store";
import CampaignTable from "src/components/campaign/CampaignTable";
import {
  getCampaignListApi,
  deleteCampaignApi,
  updateCampaignStatusApi,
  type CampaignListItem,
} from "src/api/campaign/campaignApi";
import CustomButton from "src/components/common/CustomButton";
import CampaignUpsertModal from "src/components/campaign/CampaignUpsertModal";
import CampaignLogsModal from "src/components/campaign/CampaignLogsModal";
import type { TriggerType } from "src/types/campaign";

const SIZE_OPTIONS = [20, 50, 100];

const TRIGGER_TYPE_OPTIONS = [
  { value: undefined, label: "전체" },
  { value: "AUTO", label: "자동 발급" },
  { value: "MANUAL", label: "수동 발급" },
  { value: "CLAIM", label: "직접 수령" },
  { value: "EVENT_ATTENDANCE", label: "출석체크 이벤트" },
  { value: "EVENT_RANDOM", label: "확률 기반 이벤트" },
];

export default function CampaignManagePage() {
  const { triggerType, page, size, sort, setQuery } = useCampaignStore();

  const swrKey = React.useMemo(
    () => ["campaign/list", triggerType, page, size, sort] as const,
    [triggerType, page, size, sort]
  );

  const { data, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getCampaignListApi({
        triggerType,
        page: apiPage,
        size,
        sort: sort ? [sort] : [],
      });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const list = data?.data?.content ?? [];
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const [saving, setSaving] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [logsModalOpen, setLogsModalOpen] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState<CampaignListItem | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = React.useState<number | null>(null);

  const openCreateModal = () => {
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEditModal = (item: CampaignListItem) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const openLogsModal = (campaignId: number) => {
    setSelectedCampaignId(campaignId);
    setLogsModalOpen(true);
  };

  const closeLogsModal = () => {
    setLogsModalOpen(false);
    setSelectedCampaignId(null);
  };

  const onDelete = async (item: CampaignListItem) => {
    const result = await Swal.fire({
      title: "삭제 확인",
      text: `"${item.campaignName}" 삭제하시겠습니까?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = "2000";
      },
    });
    if (!result.isConfirmed) return;

    try {
      setSaving(true);
      const res = await deleteCampaignApi(item.campaignId);
      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "삭제 실패",
          severity: "error",
        });
        return;
      }
      snackbarStore.getState().addSnackbar({ message: "삭제 완료", severity: "success" });
      await mutate();
    } finally {
      setSaving(false);
    }
  };

  const onStatusChange = async (item: CampaignListItem) => {
    const nextActive = !item.isActive;
    const result = await Swal.fire({
      title: `${nextActive ? "활성화" : "비활성화"} 확인`,
      text: `"${item.campaignName}"을(를) ${nextActive ? "활성화" : "비활성화"}하시겠습니까?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "확인",
      cancelButtonText: "취소",
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = "2000";
      },
    });
    if (!result.isConfirmed) return;

    try {
      setSaving(true);
      const res = await updateCampaignStatusApi(item.campaignId, nextActive);
      if (res.statusCode !== 1) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "상태 변경 실패",
          severity: "error",
        });
        return;
      }
      snackbarStore.getState().addSnackbar({
        message: `${nextActive ? "활성화" : "비활성화"} 완료`,
        severity: "success",
      });
      await mutate();
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitSuccess = async () => {
    await mutate();
    closeModal();
  };

  return (
    <div style={{ padding: 24 }}>
      <Card style={{ borderRadius: 12 }} bordered>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <div>
            <Typography.Title level={4} style={{ fontWeight: 900, marginBottom: 4 }}>
              캠페인 관리
            </Typography.Title>
            <Typography.Text type="secondary">
              총 {totalElements.toLocaleString()}건
            </Typography.Text>
          </div>

          <CustomButton variant="contained" onClick={openCreateModal} disabled={saving}>
            등록
          </CustomButton>
        </div>

        <Space wrap size={12} align="center" style={{ marginBottom: 16 }}>
          <Select
            value={triggerType}
            onChange={(value: TriggerType | undefined) => setQuery({ triggerType: value, page: 1 })}
            style={{ width: 200 }}
            options={TRIGGER_TYPE_OPTIONS}
            placeholder="트리거 타입"
          />

          <CustomButton variant="contained" onClick={() => mutate()} disabled={isLoading}>
            검색
          </CustomButton>

          <div style={{ flex: 1 }} />

          <Select
            value={size}
            onChange={(value) => setQuery({ size: Number(value) || 20, page: 1 })}
            style={{ width: 140 }}
            options={SIZE_OPTIONS.map((s) => ({ value: s, label: `${s}개` }))}
          />
        </Space>

        <Divider style={{ margin: "8px 0 16px" }} />

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
            <Spin />
          </div>
        ) : (
          <>
            <CampaignTable
              list={list}
              loading={false}
              disabled={saving}
              onEdit={openEditModal}
              onDelete={onDelete}
              onStatusChange={onStatusChange}
              onViewLogs={openLogsModal}
            />

            <div style={{ display: "flex", justifyContent: "center", paddingTop: 16 }}>
              <Pagination
                current={page}
                total={Math.max(1, totalElements)}
                pageSize={size}
                onChange={(nextPage) => setQuery({ page: nextPage })}
                showSizeChanger={false}
                disabled={isLoading || saving}
              />
            </div>
          </>
        )}
      </Card>

      <CampaignUpsertModal
        open={modalOpen}
        editingItem={editingItem}
        onClose={closeModal}
        onSuccess={handleSubmitSuccess}
      />

      {selectedCampaignId && (
        <CampaignLogsModal
          open={logsModalOpen}
          campaignId={selectedCampaignId}
          onClose={closeLogsModal}
        />
      )}
    </div>
  );
}
